import React from 'react';

const formatCurrency = (amount) => {
  return Math.round(amount || 0).toLocaleString('sv-SE') + ' kr';
};

/**
 * Sub-komponent: Header med expand/collapse
 */
const PaymentPlanHeader = ({ isExpanded, onToggle }) => (
  <button
    onClick={onToggle}
    className="w-full p-4 sm:p-6 flex items-center justify-between hover:bg-gray-50 transition-colors"
  >
    <h2 className="text-lg font-semibold text-leadme-gray-dark">Betalplan</h2>
    <svg
      className={`w-5 h-5 text-gray-500 transform transition-transform ${
        isExpanded ? 'rotate-180' : ''
      }`}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
    </svg>
  </button>
);

/**
 * Sub-komponent: Tabellinnehåll
 */
const PaymentPlanContent = ({ paymentPlan }) => {
  const { installments = [], type } = paymentPlan;
  const hasROT = installments.some(inst => inst.rotAvdrag > 0);
  // Året visas bara när planen går över ett årsskifte och ROT fördelats per år —
  // annars säger kolumnen ingenting kunden behöver.
  const visaAr = hasROT && new Set(installments.map(i => i.ar).filter(Boolean)).size > 1;

  const typeLabel = type === 'varannanVecka' ? 'Varannan vecka' : 'Prestationsbunden (lyftplan)';

  return (
    <div className="px-3 sm:px-6 pb-6">
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <p className="text-sm text-gray-600 mb-4">
          Faktureringstyp: <span className="font-medium text-gray-900">{typeLabel}</span>
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="text-left px-2 py-1.5 sm:px-3 sm:py-2 font-medium text-gray-600">#</th>
                <th className="text-left px-2 py-1.5 sm:px-3 sm:py-2 font-medium text-gray-600">Beskrivning</th>
                {visaAr && <th className="text-left px-2 py-1.5 sm:px-3 sm:py-2 font-medium text-gray-600">År</th>}
                <th className="text-right px-2 py-1.5 sm:px-3 sm:py-2 font-medium text-gray-600">Andel</th>
                <th className="text-right px-2 py-1.5 sm:px-3 sm:py-2 font-medium text-gray-600">Belopp</th>
                {hasROT && <th className="text-right px-2 py-1.5 sm:px-3 sm:py-2 font-medium text-gray-600">ROT-avdrag</th>}
                <th className="text-right px-2 py-1.5 sm:px-3 sm:py-2 font-medium text-gray-600">Att betala</th>
              </tr>
            </thead>
            <tbody>
              {installments.map((inst) => (
                <tr key={inst.nummer} className="border-t">
                  <td className="px-2 py-1.5 sm:px-3 sm:py-2 text-gray-500">{inst.nummer}</td>
                  <td className="px-2 py-1.5 sm:px-3 sm:py-2">{inst.beskrivning || '-'}</td>
                  {visaAr && <td className="px-2 py-1.5 sm:px-3 sm:py-2 text-gray-600">{inst.ar || '-'}</td>}
                  <td className="px-2 py-1.5 sm:px-3 sm:py-2 text-right">{(inst.procent || 0).toFixed(1)}%</td>
                  <td className="px-2 py-1.5 sm:px-3 sm:py-2 text-right">{formatCurrency(inst.belopp)}</td>
                  {hasROT && <td className="px-2 py-1.5 sm:px-3 sm:py-2 text-right text-green-700">-{formatCurrency(inst.rotAvdrag)}</td>}
                  <td className="px-2 py-1.5 sm:px-3 sm:py-2 text-right font-medium">{formatCurrency(inst.attBetala)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-gray-50 border-t-2 font-semibold">
              <tr>
                <td className="px-2 py-1.5 sm:px-3 sm:py-2" colSpan={visaAr ? 3 : 2}>Summa</td>
                <td className="px-2 py-1.5 sm:px-3 sm:py-2 text-right">
                  {installments.reduce((s, i) => s + (i.procent || 0), 0).toFixed(1)}%
                </td>
                <td className="px-2 py-1.5 sm:px-3 sm:py-2 text-right">
                  {formatCurrency(installments.reduce((s, i) => s + (i.belopp || 0), 0))}
                </td>
                {hasROT && (
                  <td className="px-2 py-1.5 sm:px-3 sm:py-2 text-right text-green-700">
                    -{formatCurrency(installments.reduce((s, i) => s + (i.rotAvdrag || 0), 0))}
                  </td>
                )}
                <td className="px-2 py-1.5 sm:px-3 sm:py-2 text-right">
                  {formatCurrency(installments.reduce((s, i) => s + (i.attBetala || 0), 0))}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>
    </div>
  );
};

/**
 * PaymentPlanSection - Visar betalplan i PU/Offert
 * Placeras mellan WorkDescriptionSection och TermsSection
 */
const PaymentPlanSection = ({ paymentPlan, isExpanded, onToggle, isCollapsed, ...rest }) => {
  // Stödjer både isExpanded och isCollapsed props (PU vs Offert)
  const expanded = isExpanded !== undefined ? isExpanded : (isCollapsed !== undefined ? !isCollapsed : true);
  const handleToggle = onToggle || rest.onToggle;

  if (!paymentPlan?.installments?.length) return null;

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
      <PaymentPlanHeader isExpanded={expanded} onToggle={handleToggle} />
      {expanded && <PaymentPlanContent paymentPlan={paymentPlan} />}
    </div>
  );
};

export default PaymentPlanSection;
